import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';

const components: Components = {
	h1: ({ children }) => (
		<h1 className="text-xl font-semibold text-primary-text mt-6 mb-3 first:mt-0 leading-snug">{children}</h1>
	),
	h2: ({ children }) => (
		<h2 className="text-lg font-semibold text-primary-text mt-5 mb-2.5 leading-snug">{children}</h2>
	),
	h3: ({ children }) => (
		<h3 className="text-base font-semibold text-primary-text mt-4 mb-2 leading-snug">{children}</h3>
	),
	h4: ({ children }) => (
		<h4 className="text-sm font-semibold text-primary-text mt-3 mb-1.5 leading-snug">{children}</h4>
	),
	p: ({ children }) => (
		<p className="text-ui text-primary-text mb-3 last:mb-0 leading-relaxed">{children}</p>
	),
	ul: ({ children }) => (
		<ul className="list-disc pl-5 mb-3 space-y-1.5 text-ui text-primary-text marker:text-primary">{children}</ul>
	),
	ol: ({ children }) => (
		<ol className="list-decimal pl-5 mb-3 space-y-1.5 text-ui text-primary-text marker:font-medium">{children}</ol>
	),
	li: ({ children }) => <li className="leading-relaxed pl-0.5">{children}</li>,
	strong: ({ children }) => <strong className="font-semibold text-primary-text">{children}</strong>,
	em: ({ children }) => <em className="italic text-secondary-text">{children}</em>,
	hr: () => <hr className="my-5 border-0 border-t border-tertiary-background" />,
	blockquote: ({ children }) => (
		<blockquote className="border-l-4 border-primary/40 pl-4 py-0.5 my-3 text-ui text-secondary-text bg-primary-background/50 rounded-r-lg">
			{children}
		</blockquote>
	),
	a: ({ href, children }) => (
		<a
			href={href}
			className="text-primary font-medium underline underline-offset-2 hover:opacity-80"
			target="_blank"
			rel="noopener noreferrer"
		>
			{children}
		</a>
	),
	code: ({ className, children }) => {
		const isBlock = Boolean(className?.includes('language-'));
		if (isBlock) {
			return (
				<code className={`${className || ''} font-jetbrains-mono text-sm`}>{children}</code>
			);
		}
		return (
			<code className="bg-tertiary-background/70 px-1.5 py-0.5 rounded text-sm font-jetbrains-mono text-primary-text">
				{children}
			</code>
		);
	},
	pre: ({ children }) => (
		<pre className="bg-primary-background border border-tertiary-background/80 p-3 rounded-xl overflow-x-auto mb-3 text-sm">
			{children}
		</pre>
	),
	table: ({ children }) => (
		<div className="overflow-x-auto my-4 rounded-lg border border-tertiary-background">
			<table className="min-w-full text-ui text-primary-text border-collapse">{children}</table>
		</div>
	),
	thead: ({ children }) => <thead className="bg-secondary-background">{children}</thead>,
	th: ({ children }) => (
		<th className="border-b border-tertiary-background px-3 py-2 text-left font-semibold text-sm">{children}</th>
	),
	td: ({ children }) => <td className="border-b border-tertiary-background/60 px-3 py-2 align-top">{children}</td>,
	tr: ({ children }) => <tr className="even:bg-primary-background/40">{children}</tr>,
};

export interface ExplainerMarkdownContentProps {
	markdown: string;
}

const ExplainerMarkdownContent: React.FC<ExplainerMarkdownContentProps> = ({ markdown }) => {
	return (
		<div className="feature-explainer-md text-ui">
			<ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
				{markdown}
			</ReactMarkdown>
		</div>
	);
};

export default ExplainerMarkdownContent;
